import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSideTittel } from "@/hooks/useSideTittel";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TemaVelgar } from "@/components/TemaVelgar";
import { BarChart3, Shield, MapPin, TrendingUp, Building2, LogIn } from "lucide-react";

const LandingPage = () => {
  useSideTittel(
    "Tala bak kommunekuttet",
    "Kommuneopprøret viser gjeld, driftsresultat, eigedomsskatt, bemanning i barnehage, skule og eldreomsorg for alle 357 norske kommunar – med fylkes-, storby- og landssnitt."
  );
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect logged-in users to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const features = [
    {
      icon: BarChart3,
      title: "Økonomisk oversikt",
      description: "Detaljert innsikt i inntektene til kommunen, utgifter og investeringar",
    },
    {
      icon: TrendingUp,
      title: "Historiske data",
      description: "Følg utviklinga over tid med data frå SSB",
    },
    {
      icon: MapPin,
      title: "Heile Noreg",
      description: "Dekkjer alle kommunar i alle fylke",
    },
    {
      icon: Shield,
      title: "Offentleg data",
      description: "Transparent tilgang til kommunal økonomidata",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]">
        <div className="animate-pulse text-[#4B647F]">Lastar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8FB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E9F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-[#103766]" strokeWidth={1.5} />
              <h1 className="text-xl font-semibold text-[#1F2A36]">
                Kommuneopprøret
              </h1>
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/sammenlign")}
                data-testid="compare-btn"
                className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Samanlikn
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={login}
                data-testid="login-btn"
                className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Logg inn
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1560698862-c340d3c8bf38?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1F2A36] mb-6">
              Kommuneopprøret
              <span className="block text-[#103766]">Tala bak kommunekuttet</span>
            </h1>
            <p className="text-lg md:text-xl text-[#4B647F] max-w-2xl mx-auto mb-10 leading-relaxed">
              Utforsk økonomisk data for alle kommunane i Noreg. Transparent innsikt i
              offentleg økonomi basert på offisielle tal frå SSB.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              data-testid="get-started-btn"
              className="bg-[#103766] hover:bg-[#1B4E8A] text-white px-8 py-6 text-lg rounded-md font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            >
              Utforsk kommunedata
            </Button>
          </div>
        </div>
      </div>

      <TemaVelgar />

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#1F2A36] mb-4">
            Omfattande kommunedata
          </h2>
          <p className="text-[#4B647F] max-w-xl mx-auto">
            Alt du treng for å forstå den økonomiske situasjonen til kommunen
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature) => (
            <Card
              key={feature.title}
              className="bg-white border border-[#E6E9F0] rounded-lg transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-[#103766]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#103766]" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-medium text-[#1F2A36] mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#4B647F] leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Data Sources Section */}
      <div className="bg-white border-t border-[#E6E9F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
              Datakjelde
            </p>
            <p className="text-[#4B647F]">
              All økonomisk data blir henta frå Statistisk Sentralbyrå (SSB)
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1F2A36] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-[#8AA4BF]">
            © {new Date().getFullYear()} Kommuneopprøret. Open kjeldekode.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
